import { useToast } from '@repo/design-system/index'
import { useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import { STACK_ACTIVITY_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { useOnclickHeartPacket } from '../../../modules/hooks/common/useOnclickHeartPacket'
import LinkLocale from '../../../modules/i18n/LinkLocale'
import { n_material } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { useLessonsStack } from '../../../pages/[lang]/webview/lessons'
import { useMaterialsStack } from '../../../pages/[lang]/webview/materials'
import SelectMaterialCard from '../lessons/materials/SelectMaterialCard'

interface RelatedContentsProps {
  relatedContents: any
  isBorderLine?: boolean
}

const MaterialRelatedContents = ({ relatedContents, isBorderLine = true }: RelatedContentsProps) => {
  const { t } = useTranslation()
  const isWebView = useIsWebview()
  const { sendClickEvent } = useEventTracking()

  const { useLessonsFlow } = useLessonsStack()
  const { push: lessonPush } = useLessonsFlow()
  const lessonRouter = useAppRouter({ stackPush: lessonPush })

  const { useMaterialsFlow } = useMaterialsStack()
  const { push: materialPush } = useMaterialsFlow()
  const materialRouter = useAppRouter({ stackPush: materialPush })

  const [packetItems, setPacketItems] = useState(relatedContents?.course || [])
  const { toastInfo } = useToast()
  const onClickHeartPacket = useOnclickHeartPacket({
    isList: true,
    packetItems: packetItems,
    setPacketItems: setPacketItems,
    onAdded: () => toastInfo(t(n_material.toast_book_mark)),
    onDeleted: () => toastInfo(t(n_material.toast_delete_book_mark)),
  })

  return (
    <>
      {packetItems.length > 0 && (
        <>
          {isBorderLine && <div className="border-t-1 mb-[24px] h-[8px] w-full border-gray-100 bg-gray-50" />}
          <div className="">
            <div className="Title1-20Bd">{t(n_material.related)}</div>
            <div className="mt-[12px] grid grid-cols-1 gap-[16px] lg:grid-cols-2">
              {packetItems?.slice(0, 2).map((material, i) => {
                return (
                  <>
                    {isWebView ? (
                      <div
                        key={i}
                        className="cursor-pointer"
                        onClick={() => {
                          sendClickEvent({ button_name: 'material_card', object_id: material.id })
                          lessonRouter.asPath.includes('/lessons')
                            ? lessonRouter.push('', {
                                stackActivity: STACK_ACTIVITY_NAME.LESSON_MATERIAL_DETAIL_ACTIVITY,
                                stackSendData: {
                                  materialId: material.id,
                                  lessonId: lessonRouter.query.lessonId,
                                },
                              })
                            : materialRouter.push('', {
                                stackActivity: STACK_ACTIVITY_NAME.MATERIAL_DETAIL_ACTIVITY,
                                stackSendData: {
                                  materialId: material.id,
                                },
                              })
                        }}
                      >
                        <div key={`packet-${i}`}>
                          <SelectMaterialCard
                            horizontal={true}
                            onClickHeart={(e) => onClickHeartPacket(e, material)}
                            title={material.title}
                            hashtag={material.hashtag}
                            subtitle={material.subtitle}
                            is_mine={material.is_mine || material.is_my_course}
                            category={material.category}
                            image_url={material.image_url}
                            is_revised={material.is_revised}
                            is_today={material.is_today}
                            level={material.level}
                            audioLength={material.mp3_running_time_in_minute}
                            isList={true}
                            key={material.id}
                          />
                        </div>
                      </div>
                    ) : (
                      <LinkLocale
                        key={i}
                        href={
                          lessonRouter.asPath.includes('/lessons')
                            ? urls.student.newPortal.lessons.materialSelect(material.id)
                            : urls.student.newPortal.materials.detail(material.id)
                        }
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation()
                          sendClickEvent({ button_name: 'material_card', object_id: material.id })
                        }}
                      >
                        <SelectMaterialCard
                          horizontal={true}
                          onClickHeart={(e) => onClickHeartPacket(e, material)}
                          title={material.title}
                          hashtag={material.hashtag}
                          subtitle={material.subtitle}
                          is_mine={material.is_mine || material.is_my_course}
                          category={material.category}
                          image_url={material.image_url}
                          is_revised={material.is_revised}
                          is_today={material.is_today}
                          level={material.level}
                          audioLength={material.mp3_running_time_in_minute}
                          isList={true}
                          key={material.id}
                        />
                      </LinkLocale>
                    )}
                  </>
                )
              })}
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default MaterialRelatedContents
